<script setup lang="ts">
import { computed } from 'vue';
import type { RelationshipPill } from './types';

const props = defineProps<{ relationships: RelationshipPill[] }>();

/**
 * Builds a link that keeps the current page but switches the active project,
 * mirroring the legacy GroupRelationshipPillsBlock (gid query param, optional
 * external host).
 */
function pillHref(pill: RelationshipPill): string {
  const url = new URL(window.location.href);
  url.searchParams.set('gid', String(pill.id));
  if (pill.host) {
    return `https://${pill.host}${url.pathname}${url.search}`;
  }
  return `${url.pathname}${url.search}`;
}

const hasRelationships = computed(() => props.relationships.length > 0);
</script>

<template>
  <section class="pp-section pp-relationships">
    <div class="pp-section-title">Project Relationship</div>

    <div v-if="!hasRelationships" class="text-muted small">
      This page is not related to any project yet.
    </div>

    <div v-else class="d-flex flex-wrap gap-2">
      <a
        v-for="pill in relationships"
        :key="pill.id"
        :href="pillHref(pill)"
        class="pp-project-pill"
        :class="{
          'pp-project-public': pill.is_default,
          'pp-project-current': pill.is_current,
        }"
        :title="pill.is_default ? 'Public project' : pill.label"
      >
        <span v-if="pill.is_current" class="pp-current-dot" aria-hidden="true"></span>
        {{ pill.label }}
      </a>
    </div>
  </section>
</template>

<style scoped>
.pp-project-pill {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.3rem 0.85rem;
  border-radius: 1rem;
  font-weight: 700;
  font-size: 0.85rem;
  text-decoration: none;
  color: #fff;
  background: #0dcaf0; /* info — non-default projects */
}
.pp-project-pill:hover {
  filter: brightness(0.95);
  text-decoration: underline;
}
.pp-project-public {
  background: #dc3545; /* danger — the public/default project */
}
.pp-current-dot {
  width: 0.5rem;
  height: 0.5rem;
  border-radius: 50%;
  background: #198754;
  box-shadow: 0 0 0 2px rgba(255, 255, 255, 0.85);
}
</style>
